/** Load the real LVGL 8.3 WASM preview and paint layout widgets into ImageData. */
import { Widget } from './types';

type LvglModule = {
  _vcs_lvgl_init: (w: number, h: number) => number;
  _vcs_lvgl_clear: () => void;
  _vcs_lvgl_begin_container: (x: number, y: number, w: number, h: number, bg: number, border: number) => void;
  _vcs_lvgl_end_container: () => void;
  _vcs_lvgl_add_label: (x: number, y: number, w: number, h: number, text: number, color: number, align: number) => void;
  _vcs_lvgl_add_icon: (x: number, y: number, w: number, h: number, text: number, color: number) => void;
  _vcs_lvgl_add_button: (x: number, y: number, w: number, h: number, text: number, color: number, bg: number) => void;
  _vcs_lvgl_add_switch: (x: number, y: number, w: number, h: number, text: number, color: number, bg: number) => void;
  _vcs_lvgl_add_progress: (x: number, y: number, w: number, h: number, text: number, color: number, bg: number) => void;
  _vcs_lvgl_add_gauge: (x: number, y: number, w: number, h: number, text: number, color: number, bg: number) => void;
  _vcs_lvgl_render: () => void;
  _vcs_lvgl_framebuffer: () => number;
  _vcs_lvgl_width: () => number;
  _vcs_lvgl_height: () => number;
  _malloc: (n: number) => number;
  _free: (p: number) => void;
  stringToUTF8: (str: string, ptr: number, max: number) => void;
  HEAPU8: Uint8Array;
};

declare global {
  interface Window {
    createVcsLvgl?: (opts?: { locateFile?: (path: string) => string }) => Promise<LvglModule>;
  }
}

let modulePromise: Promise<LvglModule> | null = null;
let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.createVcsLvgl) {
    return Promise.resolve();
  }
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${process.env.PUBLIC_URL || ''}/lvgl-viewer/vcs-lvgl.js`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load LVGL viewer'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

async function getModule(): Promise<LvglModule> {
  await loadScript();
  if (!modulePromise) {
    const factory = window.createVcsLvgl;
    if (!factory) {
      throw new Error('createVcsLvgl missing');
    }
    modulePromise = factory({
      locateFile: (path) => `${process.env.PUBLIC_URL || ''}/lvgl-viewer/${path}`,
    });
  }
  return modulePromise;
}

function cstr(mod: LvglModule, value: string | undefined, fallback = ''): number {
  const text = String(value ?? fallback);
  const bytes = text.length * 4 + 8;
  const ptr = mod._malloc(bytes);
  mod.stringToUTF8(text, ptr, bytes);
  return ptr;
}

function freeAll(mod: LvglModule, ptrs: number[]) {
  ptrs.forEach((p) => {
    if (p) mod._free(p);
  });
}

function paintWidgets(mod: LvglModule, widgets: Widget[]) {
  for (const widget of widgets) {
    const type = String(widget.type || '');
    const x = Number(widget.x) || 0;
    const y = Number(widget.y) || 0;
    const w = Number(widget.width) || 80;
    const h = Number(widget.height) || 40;
    const ptrs: number[] = [];
    try {
      if (type === 'container') {
        const bg = cstr(mod, widget.background, 'white');
        ptrs.push(bg);
        mod._vcs_lvgl_begin_container(x, y, w, h, bg, widget.border === false ? 0 : 1);
        if (widget.children?.length) {
          paintWidgets(mod, widget.children);
        }
        mod._vcs_lvgl_end_container();
        continue;
      }
      if (type === 'label') {
        const text = cstr(mod, widget.text || widget.id, 'Label');
        const color = cstr(mod, widget.color, 'black');
        const align = cstr(mod, widget.align, 'left');
        ptrs.push(text, color, align);
        mod._vcs_lvgl_add_label(x, y, w, h, text, color, align);
      } else if (type === 'icon') {
        const text = cstr(mod, widget.text, '◆');
        const color = cstr(mod, widget.color, 'black');
        ptrs.push(text, color);
        mod._vcs_lvgl_add_icon(x, y, w, h, text, color);
      } else if (type === 'button') {
        const text = cstr(mod, widget.text || widget.id, 'Button');
        const color = cstr(mod, widget.color, 'white');
        const bg = cstr(mod, widget.background, 'blue');
        ptrs.push(text, color, bg);
        mod._vcs_lvgl_add_button(x, y, w, h, text, color, bg);
      } else if (type === 'switch') {
        const text = cstr(mod, widget.text, '0');
        const color = cstr(mod, widget.color, 'blue');
        const bg = cstr(mod, widget.background, 'grey');
        ptrs.push(text, color, bg);
        mod._vcs_lvgl_add_switch(x, y, w, h, text, color, bg);
      } else if (type === 'progress') {
        const text = cstr(mod, widget.text, '0');
        const color = cstr(mod, widget.color, 'black');
        const bg = cstr(mod, widget.background, 'white');
        ptrs.push(text, color, bg);
        mod._vcs_lvgl_add_progress(x, y, w, h, text, color, bg);
      } else if (type === 'gauge') {
        const text = cstr(mod, widget.text, '0');
        const color = cstr(mod, widget.color, 'black');
        const bg = cstr(mod, widget.background, 'white');
        ptrs.push(text, color, bg);
        mod._vcs_lvgl_add_gauge(x, y, w, h, text, color, bg);
      }
    } finally {
      freeAll(mod, ptrs);
    }
  }
}

function strokeSelection(data: Uint8ClampedArray, width: number, height: number, widgets: Widget[], selectedId?: string, ox = 0, oy = 0) {
  for (const widget of widgets) {
    const x = ox + (Number(widget.x) || 0);
    const y = oy + (Number(widget.y) || 0);
    const w = Number(widget.width) || 80;
    const h = Number(widget.height) || 40;
    if (selectedId && widget.id && widget.id === selectedId) {
      const r = 33;
      const g = 150;
      const b = 243;
      for (let i = 0; i < w; i++) {
        setRgb(data, width, height, x + i, y, r, g, b);
        setRgb(data, width, height, x + i, y + h - 1, r, g, b);
      }
      for (let j = 0; j < h; j++) {
        setRgb(data, width, height, x, y + j, r, g, b);
        setRgb(data, width, height, x + w - 1, y + j, r, g, b);
      }
    }
    if (widget.children?.length) {
      strokeSelection(data, width, height, widget.children, selectedId, x, y);
    }
  }
}

function setRgb(data: Uint8ClampedArray, width: number, height: number, x: number, y: number, r: number, g: number, b: number) {
  if (x < 0 || y < 0 || x >= width || y >= height) return;
  const i = (y * width + x) * 4;
  data[i] = r;
  data[i + 1] = g;
  data[i + 2] = b;
  data[i + 3] = 255;
}

export async function lvglNativeRenderFrame(
  width: number,
  height: number,
  widgets: Widget[],
  selectedId?: string,
): Promise<ImageData | null> {
  try {
    const mod = await getModule();
    if (!mod._vcs_lvgl_init(width, height)) {
      return null;
    }
    mod._vcs_lvgl_clear();
    paintWidgets(mod, widgets);
    mod._vcs_lvgl_render();
    const ptr = mod._vcs_lvgl_framebuffer();
    const w = mod._vcs_lvgl_width();
    const h = mod._vcs_lvgl_height();
    if (!ptr || !w || !h) {
      return null;
    }
    const bytes = w * h * 4;
    const view = mod.HEAPU8.subarray(ptr, ptr + bytes);
    const copy = new Uint8ClampedArray(view);
    strokeSelection(copy, w, h, widgets, selectedId);
    return new ImageData(copy, w, h);
  } catch {
    return null;
  }
}

export async function lvglNativeAvailable(): Promise<boolean> {
  try {
    await getModule();
    return true;
  } catch {
    return false;
  }
}
